import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en';

// Partner-facing wording for case state.
//
// The portal must never print a raw internal enum. SIGNATURE_REQUIRED told a
// partner nothing and looked like a bug; it is also vocabulary from our
// workflow, not theirs.
//
// The map is intentionally coarse. A partner needs to know one thing: whose
// move it is. Theirs (chase the client), ours, or nobody's (done).

const LAWYER_STATUS: Record<string, string> = {
  complete: 'Complete',
  submitted: 'Submitted to tax office',
  // "Closed" does NOT mean archived: internally it means the NIF was never
  // delivered, while the case status stays LAWYER_REVIEW. Showing a partner
  // the word "Closed" would read as a successful ending, which is the
  // opposite of what happened.
  closed: 'Needs attention',
};

const WAITING_ON_CLIENT = 'Waiting on client';

// The step the client is stuck on, shown in brackets after "Waiting on
// client" so the partner knows what to chase without opening the case.
const CLIENT_STEP: Record<string, string> = {
  UPLOAD_PROOF_OF_IDENTITY: 'proof of identity',
  UPLOAD_PROOF_OF_ADDRESS: 'proof of address',
  PASSPORT_VIDEO_VALIDATION: 'passport video',
  PAYMENT_REQUIRED: 'payment',
  SIGNATURE_REQUIRED: 'signature',
  CONFIRMATION_REQUIRED: 'confirm details',
};

const CASE_STATUS: Record<string, string> = {
  LAWYER_REVIEW: 'In review',
  COMPLETED: 'Complete',
};

export const isComplete = (c: { lawyer_status?: string; status?: string }): boolean =>
  (c.lawyer_status || '').trim().toLowerCase() === 'complete' ||
  (c.status || '').trim().toUpperCase() === 'COMPLETED';

export const statusLabel = (c: { lawyer_status?: string; status?: string }): string => {
  const status = (c.status || '').trim().toUpperCase();
  const step = CLIENT_STEP[status];
  const waiting = step ? `${WAITING_ON_CLIENT} (${step})` : WAITING_ON_CLIENT;

  const lawyer = (c.lawyer_status || '').trim().toLowerCase();
  // A lawyer request usually leaves the case in LAWYER_REVIEW, so there is no
  // step to name; if the case was sent back to a client step, name it.
  if (lawyer === 'waiting on customer') return waiting;
  if (lawyer && LAWYER_STATUS[lawyer]) return LAWYER_STATUS[lawyer];

  if (step) return waiting;
  if (status && CASE_STATUS[status]) return CASE_STATUS[status];

  // Deliberately not the raw value. A status we have not mapped yet is a gap
  // in this file, and leaking UPLOAD_PROOF_OF_ADDRESS at a partner is worse
  // than saying something vague and true.
  return 'In progress';
};

// addLocale, not addDefaultLocale: the case detail page already set the
// default, and a second default is an error.
TimeAgo.addLocale(en);
const timeAgo = new TimeAgo('en-US');

export const parseTimestamp = (value: any): number | null => {
  if (!value) return null;
  // Firestore timestamps arrive as {_seconds}, plain dates as ISO strings.
  const ms = value?._seconds ? value._seconds * 1000 : Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
};

// How long the case has shown its current label, as "5h" or "3d". Null for
// finished cases and for cases whose history gives no start, rather than a
// made-up age.
export const timeInStep = (
  c: { lawyer_status?: string; status?: string; status_since?: any },
  nowMs: number = Date.now()
): string | null => {
  if (isComplete(c)) return null;
  const since = parseTimestamp(c.status_since);
  if (since === null) return null;
  return timeAgo.format(Math.min(since, nowMs), 'mini', { now: nowMs });
};
